'use client';

import { usePathname } from 'next/navigation';
import { Phone, Mail, MapPin } from 'lucide-react';
import GrvLogo from '@/components/GrvLogo';
import SocialLinkButtons from '@/components/layout/SocialLinkButtons';
import { AppLink } from '@/lib/appLink';
import { useCmsData, useSiteSettings } from '@/lib/CmsContext';
import { getNavMenuItems, isNavItemActive } from '@/lib/navMenus';
import { resolveFooterSocialLinks } from '@/lib/socialIcons';

const defaultServiceLinks = [
  { label: 'Будівництво будинків', href: '/budivnytstvo' },
  { label: 'Покрівельні роботи', href: '/budivnytstvo' },
  { label: 'Фасадні роботи', href: '/fasad' },
  { label: 'Утеплення фасадів', href: '/fasad' },
  { label: 'Ремонт та реконструкція', href: '/remont' },
  { label: 'Внутрішні роботи', href: '/remont' },
];

const defaultNavLinks = [
  { label: 'Головна', href: '/' },
  { label: 'Про Нас', href: '/about-us' },
  { label: 'Наші роботи', href: '/our-works' },
  { label: 'Контакти', href: '/contacts' },
];

export default function Footer() {
  const pathname = usePathname() || '/';
  const cms = useCmsData();
  const { logo, logoUrl, phone, phoneTel, phone2, phone2Tel, email, address, socialLinks, slogan, shortDescription, copyright } = useSiteSettings();
  const footerSocialLinks = resolveFooterSocialLinks(socialLinks);
  const serviceLinks = getNavMenuItems(cms, 'services', defaultServiceLinks);
  const navLinks = getNavMenuItems(cms, 'navigation', defaultNavLinks);

  return (
    <footer className="bg-background border-t border-border" data-grv-footer>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
        <div className="lg:col-span-1">
          <div className="mb-4 text-4xl">
            <AppLink href="/" className="inline-block" aria-label="На головну" data-grv-logo>
              <GrvLogo logoUrl={logoUrl} alt={logo} />
            </AppLink>
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            {shortDescription}
          </p>
          <SocialLinkButtons links={footerSocialLinks} />
        </div>

        {serviceLinks.length > 0 && (
          <div>
            <h4 className="text-foreground font-semibold mb-5 text-sm uppercase tracking-widest">Послуги</h4>
            <ul className="space-y-3">
              {serviceLinks.map((item) => {
                const isActive = isNavItemActive(pathname, item.href);
                return (
                  <li key={`${item.label}-${item.href}`}>
                    <AppLink
                      href={item.href}
                      data-grv-nav
                      data-active={isActive ? 'true' : undefined}
                      className="grv-footer-nav text-muted-foreground hover:text-gold text-sm transition-colors"
                    >
                      {item.label}
                    </AppLink>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div>
          <h4 className="text-foreground font-semibold mb-5 text-sm uppercase tracking-widest">Навігація</h4>
          <ul className="space-y-3">
            {navLinks.map((item) => {
              const isActive = isNavItemActive(pathname, item.href);
              return (
                <li key={`${item.label}-${item.href}`}>
                  <AppLink
                    href={item.href}
                    data-grv-nav
                    data-active={isActive ? 'true' : undefined}
                    className="grv-footer-nav text-muted-foreground hover:text-gold text-sm transition-colors"
                  >
                    {item.label}
                  </AppLink>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h4 className="text-foreground font-semibold mb-5 text-sm uppercase tracking-widest">Контакти</h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <div>
                <a href={`tel:${phoneTel}`} className="text-foreground hover:text-gold text-sm transition-colors block">{phone}</a>
                {phone2 ? (
                  <a href={`tel:${phone2Tel}`} className="text-foreground hover:text-gold text-sm transition-colors block mt-1">{phone2}</a>
                ) : null}
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <a href={`mailto:${email}`} className="text-foreground hover:text-gold text-sm transition-colors">
                {email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <span className="text-muted-foreground text-sm">{address}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs">{copyright}</p>
          <p className="text-muted-foreground text-xs">{slogan}</p>
        </div>
      </div>
    </footer>
  );
}
